import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Play, Calendar } from "lucide-react";
import { Button } from "../ui/button";
import { useEffect, useState, useRef } from "react";

const BOOKING_URL = "https://agendamiento.reservo.cl/makereserva/agenda/v0XFJkK0p04Joq5h8h11dw79n3v0yK";

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          let start = 0;
          const increment = end / (duration * 60);
          const timer = setInterval(() => {
            start += increment;
            if (start >= end) {
              setCount(end);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 1000 / 60);
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, duration, hasAnimated]);

  return <span ref={ref}>{count}{suffix}</span>;
};

export const HeroSection = () => {
  const whatsappUrl = `https://wa.me/56966951278?text=${encodeURIComponent("¡Hola! Quiero agendar mi clase de prueba en Inspirion GYM")}`;
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });
  
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  return (
    <section 
      ref={containerRef}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Video Background with Parallax */}
      <motion.div 
        className="absolute inset-0 z-0"
        style={{ y }}
      >
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          poster="https://images.unsplash.com/photo-1534438327276-14e5300c3a48?q=80&w=2070"
        >
          <source 
            src="https://videos.pexels.com/video-files/4761434/4761434-uhd_2560_1440_25fps.mp4" 
            type="video/mp4" 
          />
        </video>
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/75" />
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black" />
      </motion.div>

      {/* Animated particles/grid effect */}
      <div className="absolute inset-0 z-1 opacity-20">
        <div 
          className="w-full h-full"
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #00FF00 1px, transparent 0)`,
            backgroundSize: "50px 50px",
            animation: "pulse 4s ease-in-out infinite",
          }}
        />
      </div>

      {/* Neon accent lines */}
      <motion.div 
        className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-neon-green via-neon-cyan to-neon-magenta"
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 1, delay: 0.5 }}
      />
      <motion.div 
        className="absolute bottom-0 left-0 w-1/3 h-1 bg-neon-green" 
        style={{ boxShadow: "0 0 20px rgba(0, 255, 0, 0.8)" }}
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 0.8, delay: 1 }}
      />

      {/* Content */}
      <motion.div 
        className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20"
        style={{ opacity }}
      >
        <div className="flex flex-col lg:flex-row items-center gap-12">
          {/* Left content */}
          <div className="flex-1 text-left">
            {/* Logo */}
            <motion.img
              src="https://customer-assets.emergentagent.com/job_gym-rancagua/artifacts/vax34u1r_Logotipo%20Inspirion%20GYM%20v1_Alta%20Calidad.png"
              alt="Inspirion GYM"
              className="w-48 md:w-64 mb-8"
              initial={{ opacity: 0, y: -30, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.8, type: "spring" }}
            />

            {/* Tag */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, type: "spring", stiffness: 100 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-neon-green/30 bg-neon-green/10 text-neon-green text-sm font-semibold uppercase tracking-wider mb-6"
            >
              <Play className="w-4 h-4" fill="currentColor" />
              Entrenamiento Personalizado en Rancagua
            </motion.div>

            {/* Main Headline with staggered animation */}
            <motion.h1
              className="text-5xl sm:text-6xl lg:text-7xl font-black uppercase leading-none tracking-tighter mb-6"
            >
              <motion.span 
                className="block text-white"
                initial={{ opacity: 0, x: -100 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
              >
                Transforma
              </motion.span>
              <motion.span 
                className="block text-neon-green text-glow-green"
                initial={{ opacity: 0, x: -100 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5, duration: 0.6 }}
              >
                Tu Cuerpo
              </motion.span>
              <motion.span 
                className="block text-white italic"
                initial={{ opacity: 0, x: -100 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6, duration: 0.6 }}
              >
                De Verdad
              </motion.span>
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.6 }}
              className="text-lg sm:text-xl text-gray-300 max-w-xl mb-8 font-medium leading-relaxed"
            >
              Aquí <span className="text-neon-cyan font-bold">no entrenas solo</span>. 
              Te acompañamos paso a paso con planes diseñados para ti, 
              sin importar tu nivel o experiencia.
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.6 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Button
                asChild
                data-testid="hero-cta-booking"
                className="group relative overflow-hidden bg-neon-green hover:bg-neon-green text-black font-bold text-lg px-8 py-6 rounded-none uppercase tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-neon-green-lg"
                style={{ boxShadow: "0 0 30px rgba(0, 255, 0, 0.4)" }}
              >
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                  <span className="relative z-10 flex items-center">
                    <Calendar className="mr-2 w-5 h-5" />
                    Agenda tu clase de prueba
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-2 transition-transform duration-300" />
                  </span>
                  <motion.div
                    className="absolute inset-0 bg-white"
                    initial={{ x: "-100%" }}
                    whileHover={{ x: "100%" }}
                    transition={{ duration: 0.5 }}
                    style={{ opacity: 0.2 }}
                  />
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                data-testid="hero-cta-whatsapp"
                className="group border-2 border-neon-whatsapp bg-transparent text-neon-whatsapp hover:bg-neon-whatsapp hover:text-black font-bold text-lg px-8 py-6 rounded-none uppercase tracking-wide transition-all duration-300 hover:scale-105"
              >
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                  WhatsApp
                </a>
              </Button>
            </motion.div>
          </div>

          {/* Right content - Animated Stats */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1, duration: 0.6, type: "spring" }}
            className="hidden lg:flex flex-col gap-6"
          >
            {[
              { number: 500, suffix: "+", label: "Alumnos Transformados" },
              { number: 5, suffix: "", label: "Años de Experiencia" },
              { number: 100, suffix: "%", label: "Planes Personalizados" },
            ].map((stat, index) => (
              <motion.div 
                key={index}
                className="glass-card p-6 border-l-4 border-l-neon-green cursor-pointer"
                whileHover={{ 
                  scale: 1.05, 
                  x: 10,
                  boxShadow: "0 0 30px rgba(0, 255, 0, 0.3)"
                }}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1 + index * 0.2 }}
              >
                <div className="text-4xl font-black text-neon-green text-glow-green">
                  <AnimatedCounter end={stat.number} suffix={stat.suffix} />
                </div>
                <div className="text-sm text-gray-400 uppercase tracking-wider">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-gray-400"
      >
        <motion.span 
          className="text-xs uppercase tracking-widest"
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          Descubre más
        </motion.span>
        <div className="w-6 h-10 border-2 border-gray-600 rounded-full flex items-start justify-center p-1">
          <motion.div
            animate={{ y: [0, 16, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            className="w-1.5 h-3 bg-neon-green rounded-full"
          />
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
